import type { VehicleValuationDto } from "@gladis/shared";
import { formatEur } from "@gladis/shared";
import { EmptyState, PersonLabel, Table } from "@gladis/ui";
import { formatDate, useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

/**
 * Every observation of what this vehicle was worth, newest first.
 *
 * ==========================================================================================
 * IT IS A LIST OF STATEMENTS PEOPLE MADE, NOT A MODEL OF ANYTHING
 * ==========================================================================================
 *
 * This table is the whole of `docs/vehicles-decisions.md` §4.2 made visible. Every figure here was
 * typed by somebody on a date they chose, and the value in force between two rows is the upper one —
 * carried forward flat, never interpolated and never reduced by a percentage. A household reading
 * this can see exactly why the chart is a staircase.
 *
 * The SOURCE is shown because "a price list said so" and "I had a look on Marktplaats" are different
 * kinds of claim, and the enum has exactly those two values for the reason its schema gives: nothing
 * observes a car for free, so a third would mean "a figure this application made up".
 *
 * WHO recorded it is shown for a reason 020 settled: an observation with a departed recorder carries
 * a null id AND a null name — this table snapshots no name, as savings snapshots do not — so the row
 * genuinely does not know who it was about and gets a stand-in rather than invented initials.
 */
export function ValuationHistory({
  valuations,
}: {
  valuations: readonly VehicleValuationDto[];
}): ReactNode {
  const t = useT();

  if (valuations.length === 0) {
    return <EmptyState>{t("veh.valuation.empty")}</EmptyState>;
  }

  return (
    <Table>
      <Table.Head>
        <Table.Row>
          <Table.HeaderCell>{t("veh.valuation.colDate")}</Table.HeaderCell>
          <Table.HeaderCell numeric>{t("veh.valuation.colAmount")}</Table.HeaderCell>
          <Table.HeaderCell>{t("veh.valuation.colSource")}</Table.HeaderCell>
          <Table.HeaderCell>{t("veh.valuation.colRecordedBy")}</Table.HeaderCell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {valuations.map((valuation) => (
          <Table.Row key={valuation.id}>
            <Table.Cell>{formatDate(valuation.valuedOn)}</Table.Cell>
            <Table.Cell numeric>{formatEur(valuation.amountInEuros)}</Table.Cell>
            <Table.Cell>
              {valuation.source === "koerslijst"
                ? t("veh.valuation.sourceKoerslijst")
                : t("veh.valuation.sourceManual")}
            </Table.Cell>
            <Table.Cell>
              <PersonLabel
                label={valuation.recordedBy.displayName ?? t("veh.valuation.someoneWhoLeft")}
                isPerson={valuation.recordedBy.isPerson}
              />
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}
